import type { TransformParams, TransformResult } from '../types/interiorAI'

/**
 * Interior AI transform provider — calls Google's Gemini 2.5 Flash Image
 * REST endpoint directly from the browser with the user's own API key.
 */
const MODEL = 'gemini-2.5-flash-image'
const ENDPOINT = `https://generativelanguage.googleapis.com/v1beta/models/${MODEL}:generateContent`

export class GeminiApiError extends Error {
  status?: number
  constructor(message: string, status?: number) {
    super(message)
    this.name = 'GeminiApiError'
    this.status = status
  }
}

interface GeminiPart {
  text?: string
  inlineData?: {
    mimeType: string
    data: string
  }
}

export async function transformImageWithGemini(
  params: TransformParams,
): Promise<TransformResult> {
  const { apiKey, imageBase64, mimeType, prompt } = params

  if (!apiKey) {
    throw new GeminiApiError('Chýba Gemini API kľúč. Pridaj ho v nastaveniach nižšie.')
  }

  let response: Response
  try {
    response = await fetch(`${ENDPOINT}?key=${encodeURIComponent(apiKey)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contents: [
          {
            parts: [{ text: prompt }, { inlineData: { mimeType, data: imageBase64 } }],
          },
        ],
      }),
    })
  } catch {
    throw new GeminiApiError('Nepodarilo sa spojiť s Gemini API. Skontroluj internetové pripojenie.')
  }

  if (!response.ok) {
    let message = `Gemini API vrátilo chybu (${response.status}).`
    try {
      const errBody = await response.json()
      if (errBody?.error?.message) message = errBody.error.message
    } catch {
      // keep default message
    }
    if (response.status === 400) {
      message = 'Požiadavka bola zablokovaná alebo je neplatná. Skús inú fotku alebo uprav nastavenia.'
    } else if (response.status === 403) {
      message = 'API kľúč je neplatný alebo nemá prístup k tomuto modelu.'
    } else if (response.status === 429) {
      message = 'Prekročený limit požiadaviek. Skús to o chvíľu znova.'
    }
    throw new GeminiApiError(message, response.status)
  }

  const data = await response.json()
  const parts: GeminiPart[] = data?.candidates?.[0]?.content?.parts || []
  const imagePart = parts.find((p) => p.inlineData?.data)
  const textPart = parts.find((p) => typeof p.text === 'string')

  if (!imagePart?.inlineData) {
    throw new GeminiApiError('Gemini nevrátilo žiadny obrázok. Skús inú fotku alebo iné nastavenia.')
  }

  return {
    imageBase64: imagePart.inlineData.data,
    mimeType: imagePart.inlineData.mimeType || 'image/png',
    textNote: textPart?.text,
  }
}

/** Converts a browser File into the raw base64 payload Gemini expects (no data: prefix). */
export function fileToBase64(file: File): Promise<{ base64: string; mimeType: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      const [, base64] = result.split(',')
      resolve({ base64, mimeType: file.type || 'image/jpeg' })
    }
    reader.onerror = () => reject(new Error('Nepodarilo sa načítať súbor.'))
    reader.readAsDataURL(file)
  })
}
